import type {
  JourneyHeader,
  JourneyStop,
  StopKey,
  Journey,
} from "@/app/services/generated/journeyv3Service";
import {
  JourneyFilter,
  JourneyService,
} from "@/app/services/generated/journeyv3Service";
import type { GuidMap } from "@/app/types/common/guidcollections";
import { GuidSet } from "@/app/types/common/guidcollections";
import type { OrderSummary } from "@/app/services/generated/orderService";
import { OrderFilter } from "@/app/services/generated/orderService";
import type { UllageHeader } from "@/app/services/generated/ullageService";
import { ullageService } from "@/app/services/generated/ullageService";
import type { Visit } from "@/app/services/generated/visitService";
import { visitService } from "@/app/services/generated/visitService";
import { orderService } from "@/app/services/extensions/orderServiceExtended";
import { toGuidMap } from "@/app/apiutils";
import { BrewManError } from "@/app/errors";
import messages, { translate, keys } from "@/plugins/i18n";
import type { Vehicle } from "@/app/services/generated/vehicleService";
import type { LocalDate } from "@js-joda/core";

export type JourneyStopEntityType = OrderSummary | Visit | UllageHeader;

export interface JourneyStopWithEntity {
  entity: JourneyStopEntityType;
  stop: JourneyStop;
}

// Returns the relevant entity for a stop
export function getEntityForJourneyStop(
  stop: Readonly<JourneyStop>,
  ordersMap: Readonly<GuidMap<OrderSummary>>,
  visitsMap: Readonly<GuidMap<Visit>>,
  ullagesMap: Readonly<GuidMap<UllageHeader>>
): JourneyStopEntityType {
  const { orderId, visitId, ullageId } = stop.stopKey;

  if (orderId) {
    const orderSummary = ordersMap.get(orderId);

    if (!orderSummary) {
      throw new BrewManError(
        messages.multi_journey_map_does_not_contain_entity_for_stop
      );
    }

    return orderSummary;
  }

  if (visitId) {
    const visit = visitsMap.get(visitId);

    if (!visit) {
      throw new BrewManError(
        messages.multi_journey_map_does_not_contain_entity_for_stop
      );
    }

    return visit;
  }

  if (ullageId) {
    const ullageHeader = ullagesMap.get(ullageId);

    if (!ullageHeader) {
      throw new BrewManError(
        messages.multi_journey_map_does_not_contain_entity_for_stop
      );
    }

    return ullageHeader;
  }

  throw new BrewManError(
    messages.multi_journey_stop_does_not_have_associated_entity
  );
}

function journeyStopIsOnJourney(journey: Journey, stop: JourneyStop) {
  return journey.stopKeys.find((stopKey) => stopKey.equals(stop.stopKey));
}

export default class JourneyServiceExtended extends JourneyService {
  public getEntityIdsFromStopKeys(stopKeys: StopKey[]) {
    const orderIds = new GuidSet();
    const visitIds = new GuidSet();
    const ullageIds = new GuidSet();

    stopKeys.forEach((stopKey) => {
      if (stopKey.orderId) {
        orderIds.add(stopKey.orderId);
      }

      if (stopKey.visitId) {
        visitIds.add(stopKey.visitId);
      }

      if (stopKey.ullageId) {
        ullageIds.add(stopKey.ullageId);
      }
    });

    return {
      orderIds: Array.from(orderIds),
      visitIds: Array.from(visitIds),
      ullageIds: Array.from(ullageIds),
    };
  }

  public async getJourneyStopEntities(stops: JourneyStop[]) {
    // Collect all entity ids
    const orderIds = new GuidSet();
    const visitIds = new GuidSet();
    const ullageIds = new GuidSet();

    stops.forEach((stop) => {
      const { orderId, visitId, ullageId } = stop.stopKey;

      if (orderId) {
        orderIds.add(orderId);
      }

      if (visitId) {
        visitIds.add(visitId);
      }

      if (ullageId) {
        ullageIds.add(ullageId);
      }
    });

    // Fetch entities
    const orderFilter = new OrderFilter();
    orderFilter.limitOrderHeaderIds = Array.from(orderIds);

    const [orders, visits, ullages] = await Promise.all([
      orderService
        .getOrderSummaries(orderFilter)
        .then((result) => result.orders),
      visitService.getVisits(Array.from(visitIds)),
      ullageService.getUllageHeaders(Array.from(ullageIds)),
    ]);

    const ordersMap = toGuidMap(orders, (order) => order.id);
    const visitsMap = toGuidMap(visits, (visit) => visit.id);
    const ullagesMap = toGuidMap(ullages, (ullage) => ullage.id);

    return { ordersMap, visitsMap, ullagesMap };
  }

  public getJourneyName(
    journeyHeader: Readonly<JourneyHeader>,
    vehicle: Readonly<Vehicle> | null
  ) {
    return translate(keys.multi_journey_vehicle_with_sequence, {
      vehicle: vehicle?.name,
      sequence: journeyHeader.readOnly.sequence || "",
    }).toString();
  }

  public async getJourneyHeadersForDespatchDate(despatchDate: LocalDate) {
    const filter = new JourneyFilter();
    filter.fromDespatchDate = despatchDate;
    filter.toDespatchDate = despatchDate;
    return (await this.getJourneyHeadersByFilter(filter)).journeyHeaders;
  }

  public async getJourneyDetailsForDespatchDate(despatchDate: LocalDate) {
    const filter = new JourneyFilter();
    filter.fromDespatchDate = despatchDate;
    filter.toDespatchDate = despatchDate;
    return (await this.getJourneyDetailsByFilter(filter)).details;
  }
}

export const journeyService = new JourneyServiceExtended();
